"use client";
import Link from "@workspace/casino_pages/legacyLayer/client/components/Link";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  Drawer,
  DrawerContent,
  ErrorIcon,
  FileIcon,
  LinkIcon,
  Loader,
  ShieldIcon,
  TypographyCaption,
  TypographyH4,
  TypographyText,
  useMediaQuery,
} from "@workspace/frontend-ui";
import { useEffect, useState } from "react";
import licenseUpdate from "@/actions/licenseUpdate";

type Props = {
  state?: "default" | "loading" | "error" | "success";
  translations?: Record<string, string>;
  termsPdfUrl?: string;
  onAgree?: () => void;
  onRetry?: () => void;
  handleClose?: () => void;
};
function IconState({ state }: Props) {
  return (
    <div
      className={cn(
        "rounded-full bg-secondary p-2 w-fit",
        state === "default" && "text-primary",
        state === "error" && "text-destructive",
        state === "success" && "text-success",
      )}
    >
      {state === "error" && <ErrorIcon />}
      {(state === "default" || state === "loading") && <ShieldIcon />}
      {state === "success" && <ShieldIcon />}
    </div>
  );
}

function InnerModalContent({
  state = "default",
  translations,
  termsPdfUrl,
  onAgree,
  onRetry,
  handleClose,
}: Props) {
  const isLoading = state === "loading";
  const isError = state === "error";
  const isSuccess = state === "success";

  const t = (key: string, fallback: string) => translations?.[key] ?? fallback;

  const handleContactSupport = async () => {
    await window.proto?.show();
    await window.proto?.open();
  };

  if (isError) {
    return (
      <>
        <div
          className={cn("flex flex-col items-center gap-2 max-sm:mt-6 px-4")}
        >
          <IconState state={state} />
          <TypographyH4 isBold>
            {t("terms.anjouan.error.title", "Something went wrong")}
          </TypographyH4>
          <TypographyText
            variant="small"
            className="text-muted-foreground text-center max-w-[368px]"
          >
            {t(
              "terms.anjouan.error.description",
              "We couldn’t save your agreement. Check your connection and try again.",
            )}
          </TypographyText>
        </div>
        <Button className="h-10 mt-2" onClick={onRetry}>
          {t("terms.anjouan.error.retry", "Try Again")}
        </Button>
        <Button
          variant="outline"
          className="h-10"
          onClick={handleContactSupport}
        >
          {t("terms.anjouan.error.contactsupport", "Contact support")}
        </Button>
      </>
    );
  }

  if (isSuccess) {
    return (
      <>
        <div
          className={cn("flex flex-col items-center gap-2 max-sm:mt-6 px-4 ")}
        >
          <IconState state={state} />
          <TypographyH4 isBold>
            {t("terms.anjouan.success.title", "You’re all set")}
          </TypographyH4>
        </div>
        <Button className="h-10 mt-2" onClick={handleClose}>
          {t("terms.anjouan.success.button", "Ok")}
        </Button>
      </>
    );
  }

  // This cover only default and loading state
  return (
    <>
      <div
        className={cn(
          "flex flex-col items-center gap-2 max-sm:mt-6 px-4",
          isLoading && !isError && "opacity-40",
        )}
      >
        <IconState state={state} />
        <TypographyH4 isBold>
          {isError
            ? t("terms.anjouan.error.title", "Something went wrong")
            : t("terms.anjouan.default.title", "Our Terms have changed")}
        </TypographyH4>
        <TypographyText
          variant="small"
          className="text-muted-foreground text-center max-w-[368px]"
        >
          {t(
            "terms.anjouan.default.description",
            "Bitcasino.io now operates under a licence from Anjouan. Your balance, open bets and account stay the same.",
          )}
        </TypographyText>
      </div>
      <div className="mt-2" />
      {termsPdfUrl && (
        <Button
          asChild
          variant="unstyled"
          className="w-full py-6 font-semibold text-primary! bg-secondary rounded-lg"
        >
          <Link href={termsPdfUrl} target="_blank">
            <FileIcon />
            <TypographyText className="m-0! text-sm">
              {t("terms.anjouan.cta.link_text", "Read the updated Terms")}
            </TypographyText>
            <div className="flex-1" />
            <LinkIcon />
          </Link>
        </Button>
      )}
      <Button
        variant="default"
        className="w-full mt-2 h-10"
        onClick={onAgree}
        disabled={isLoading}
      >
        {isLoading && <Loader size="xs" />}
        {t("terms.anjouan.default.button", "Agree and continue")}
      </Button>
      <TypographyCaption
        className="mx-auto text-muted-foreground mt-1"
        noUppercase
      >
        {t("terms.anjouan.default.caption", "Required to keep betting.")}
      </TypographyCaption>
    </>
  );
}

export default function TermsModal({ translations, termsPdfUrl }: Props) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [state, setState] = useState<
    "default" | "loading" | "error" | "success"
  >("default");

  const [visible, setVisible] = useState<boolean>(true);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    licenseUpdate({ intent: "ACKNOWLEDGE" }).catch(() => {});
  }, []);

  if (!mounted) return null;

  const handleAgree = async () => {
    setState("loading");
    try {
      const { success } = await licenseUpdate({ intent: "ACCEPT" });
      setState(success ? "success" : "error");
    } catch {
      setState("error");
    }
  };

  const handleRetry = () => {
    setState("default");
  };

  const handleClose = async () => {
    setVisible(false);
  };

  if (!isDesktop) {
    return (
      <Drawer open={visible} dismissible={false}>
        <DrawerContent
          className="px-4 bg-background z-[10001]"
          overlayClassName="z-[10001]"
        >
          <div className="mt-4" />
          <InnerModalContent
            state={state}
            translations={translations}
            termsPdfUrl={termsPdfUrl}
            onAgree={handleAgree}
            handleClose={handleClose}
            onRetry={handleRetry}
          />
          <div className="pb-10" />
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={visible}>
      <DialogContent
        className="max-w-[428px]! bg-background z-[10001]"
        overlayClassName="z-[10001]"
        showCloseButton={false}
      >
        <InnerModalContent
          state={state}
          translations={translations}
          termsPdfUrl={termsPdfUrl}
          onAgree={handleAgree}
          onRetry={handleRetry}
          handleClose={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
}
